import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Formik, Form } from 'formik'
import { useTenantPartnerId } from '@labrav/common'
import { useDispatch, useSelector } from '../../store'
import { FooterV2 } from '../../common/components/FooterV2/FooterV2'
import { LoadingTypes, isLoading } from '@labrav/common'

import { ButtonV2 } from '@labrav/react-components'

import { useStyles } from './FlyoutCloudSettingsWrapper.styles'
import { getFlyoutCloudSettingsValidateSchema } from '../../common/utils/flyoutCloudSettingsValidateSchema'
import { useSellerInfo } from './useSellerInfo'
import { CloudPartnerDetails } from './AccountRegistration/CloudPartnerDetails/CloudPartnerDetails'
import { FlyOutArnDetails } from './AccountRegistration/FlyOutArnDetails/FlyOutArnDetails'
import { FlyoutCasProgramSetup } from './AccountRegistration/FlyoutCasProgramSetup/FlyoutCasProgramSetup'
import type { FlyOutCloudSettingsFormValues } from './types'
import { useLocation, useNavigate } from 'react-router-dom'
import { LoadingStateComponent } from '@labrav/common'
import {
  FlyoutOnboardingSectionName,
  ListingType,
  type Sections,
} from '../../modules/flyOutOnboarding/types'
import { useTenant } from './useTenant'
import { FlyOutListingUserType } from '../../types/external'
import {
  getFlyOutOnboardingData,
  sendFlyOutOnboardingData,
} from '../../modules/flyOutOnboarding/action'
import { CloudType } from '../../types'
import { useFlagValue } from '@labrav/flags'
import { getPageMarkerMapping } from '../../common/utils/getPageMarkerMapping'
import { useFlyOutOnboardingStatus } from '../FlyOutOnboardingWrapper/FlyOutAwsOnboarding/FlyOutOnboardingSaveButton/useFlyOutOnboardingStatus'
import { CloudPartnerDetailsWrapper } from '../FlyOutCloudSettings/CloudPartnerDetailsWrapper/CloudPartnerDetailsWrapper'
import type { DataFeedsOwnS3BucketChoice } from '../FlyOutCloudSettings/FlyOutArnAndDataFeedsWrapper/types'
import { FlyOutArnAndDataFeedsWrapper } from '../FlyOutCloudSettings/FlyOutArnAndDataFeedsWrapper/FlyOutArnAndDataFeedsWrapper'
import { FlyOutArnDetailsWrapper } from '../FlyOutCloudSettings/FlyOutArnDetailsWrapper/FlyOutArnDetailsWrapper'
import { FlyoutCasProgramSetupWrapper } from '../FlyOutCloudSettings/FlyoutCasProgramSetupWrapper/FlyoutCasProgramSetupWrapper'
import type { StackDeploymentType } from '../FlyOutCloudSettings/FlyOutArnAndDataFeedsWrapper/types'
import {
  mapFlyoutCloudSettingsToFlyoutListingData,
  mapFlyoutListingDataToFlyoutCloudSettings,
} from './helpers'

export interface FlyoutCloudSettingsWrapperProps {
  accountId?: string
}

export const FlyoutCloudSettingsWrapper: React.FC<
  FlyoutCloudSettingsWrapperProps
> = ({ accountId: accountId }) => {
  const { pathname } = useLocation()
  const isExternalRoute = pathname.split('/').includes('external-apps')
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [connectionTested, setConnectionTested] = useState(false)
  const { value: isMarketplaceCloudSettingsRevampEnabled } = useFlagValue(
    'marketplaceCloudSettingsRevamp'
  )
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )
  const flyoutCloudSettingsValidateSchema = useMemo(
    () =>
      getFlyoutCloudSettingsValidateSchema(
        Boolean(isMarketplaceCloudSettingsRevampEnabled),
        Boolean(isOnboardingImprovementsEnabled)
      ),
    [isMarketplaceCloudSettingsRevampEnabled, isOnboardingImprovementsEnabled]
  )
  const tenantData = useTenant()
  const tenantPartnerId = useTenantPartnerId()

  useEffect(() => {
    if (accountId && tenantPartnerId) {
      dispatch(
        getFlyOutOnboardingData(accountId, tenantPartnerId, CloudType.AWS)
      )
    }
  }, [accountId, tenantPartnerId])

  const flyOutOnboardingForm = useSelector<Sections>(
    state => state.flyOutOnboarding.onboardingForm
  )

  const { loading: sellerInfoLoading, flyoutSellerInfo } = useSellerInfo()
  const noOps = () => void 0
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const location = useLocation()

  useEffect(() => {
    if ((location.state as { scrollToSetup?: boolean } | null)?.scrollToSetup) {
      const element = document.getElementById('flyoutCasProgramSetupWrapper')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [location])
  const { onboardingComplete: flyoutOnboardingComplete } =
    useFlyOutOnboardingStatus()
  const flyoutOnboardingFormSections: Sections = {
    [FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS]: {
      sellerOriginCountry: '',
      companyName: tenantData?.companyName || '',
      companyWebsite: tenantData?.websiteUrl || '',
      industry: tenantData?.industry || '',
      partnershipType: [],
      teamMembers: {
        admins: [],
        staff: [],
      },
      listingType: 'NEW' as ListingType,
      formFilled: true,
    },
    [FlyoutOnboardingSectionName.CLOUD_SETTINGS]: {
      cloudProvider: CloudType.AWS,
      cloudAccountId:
        flyOutOnboardingForm?.cloudSettings?.cloudAccountId ||
        flyoutSellerInfo?.awsAccountId ||
        '',
      cloudAccountName:
        flyOutOnboardingForm?.cloudSettings?.cloudAccountName ||
        flyoutSellerInfo?.awsAccountName ||
        '',
      cloudRoleArn:
        flyOutOnboardingForm?.cloudSettings?.cloudRoleArn ||
        flyoutSellerInfo?.sellerMetadata?.awsRoleArn ||
        '',
      cloudCasS3BucketName:
        flyOutOnboardingForm?.cloudSettings?.cloudCasS3BucketName ||
        flyoutSellerInfo?.sellerMetadata?.awsCasS3BucketName ||
        '',
      cloudCasSnsTopic:
        flyOutOnboardingForm?.cloudSettings?.cloudCasSnsTopic ||
        flyoutSellerInfo?.sellerMetadata?.awsCasSnsTopic ||
        '',
      cloudKmsKeyArn:
        flyOutOnboardingForm?.cloudSettings?.cloudKmsKeyArn ||
        flyoutSellerInfo?.sellerMetadata?.awsKmsKeyArn ||
        '',
      connectionTested:
        flyOutOnboardingForm?.cloudSettings?.connectionTested || false,
      formFilled: flyOutOnboardingForm?.cloudSettings?.formFilled || true,
      testConnectionStatus:
        flyOutOnboardingForm?.cloudSettings?.testConnectionStatus || 'standBy',
      currencies: flyOutOnboardingForm?.cloudSettings?.currencies ||
        flyoutSellerInfo?.currencies || ['USD'],
      formUpdated: true,
      countryRegistered: flyoutSellerInfo?.countryRegistered || '',
      isOwnS3Bucket:
        flyOutOnboardingForm?.cloudSettings?.isOwnS3Bucket ?? false,
      stackType:
        flyOutOnboardingForm?.cloudSettings?.stackType ??
        flyoutSellerInfo?.stackType ??
        'cloudformation',
      terraformConnectionValidated:
        flyOutOnboardingForm?.cloudSettings?.terraformConnectionValidated ??
        flyoutSellerInfo?.terraformConnectionValidated ??
        false,
      cloudExternalId:
        flyOutOnboardingForm?.cloudSettings?.cloudExternalId || '',
    },
    [FlyoutOnboardingSectionName.PAGE_MARKER]: {
      page1: true,
      page2: true,
    },
    [FlyoutOnboardingSectionName.TERMS_AND_CONDITIONS]: {
      termsAndConditions: [
        {
          label: 'Terms and Conditions',
          value: true,
          type: 'checkbox',
        },
      ],
    },
    [FlyoutOnboardingSectionName.LOCK_METADATA]: {
      lockedBy: '',
      lastActivityAt: '',
      lockAcquisitionProbe: false,
      windowId: '',
      lockedByName: '',
      editingEnabled: true,
      lockByEmail: '',
    },
    [FlyoutOnboardingSectionName.GENERAL_INFORMATION]: {
      companyName: tenantData?.companyName || '',
      logoUrl: '',
    },
    [FlyoutOnboardingSectionName.LANDING_PAGE_DETAILS]: {
      companyName: '',
      sellerName: '',
      companyWebsite: '',
      logoUrl: '',
    },
  }

  const sendSellerNewOnboardingDataFromSettings = useCallback(
    async (sellerData: FlyOutCloudSettingsFormValues) => {
      const transformedCloudSettingsData =
        mapFlyoutListingDataToFlyoutCloudSettings(sellerData, accountId)
      const onboardingFormDetails = {
        ...flyoutOnboardingFormSections,
        labraPartnerDetails: {
          ...flyoutOnboardingFormSections[
            FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS
          ],
          partnershipType: sellerData?.sellerType || ['ISV'],
        },
        cloudSettings: transformedCloudSettingsData,
      }

      await dispatch(
        sendFlyOutOnboardingData({
          partnerId: accountId as string,
          tenantPartnerId: tenantPartnerId as string,
          isAutoSave: false,
          onboardingForm: onboardingFormDetails,
          pageMarkerMapping: getPageMarkerMapping(),
          page: FlyoutOnboardingSectionName.CLOUD_SETTINGS,
          status: 'lockAcquire',
          isOnboarded: flyoutOnboardingComplete,
        })
      )
    },
    [dispatch, accountId, flyOutOnboardingForm]
  )

  if (sellerInfoLoading) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingStateComponent
          heading="Loading your AWS settings"
          description="It may take a few moments, please wait..."
        />
      </div>
    )
  }
  const initialValues = mapFlyoutCloudSettingsToFlyoutListingData(
    flyoutOnboardingFormSections[FlyoutOnboardingSectionName.CLOUD_SETTINGS],
    flyoutOnboardingFormSections[
      FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS
    ].partnershipType ||
      flyoutSellerInfo?.sellerType || ['ISV'],
    flyoutSellerInfo?.origin || ('NEW' as FlyOutListingUserType)
  )
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={flyoutCloudSettingsValidateSchema}
      validateOnMount={true}
      onSubmit={noOps}
      enableReinitialize
    >
      {formikProps => {
        const isTerraformStack =
          Boolean(isOnboardingImprovementsEnabled) &&
          formikProps.values.stackType === 'terraform'
        const disabledDataFeedsInOwnS3BucketChoices: DataFeedsOwnS3BucketChoice[] =
          [formikProps.values.isOwnS3Bucket ? 'no' : 'yes']
        const connectionTestFormikStatus = formikProps.status as
          | {
              arnConnectionStatus?: string
              failedConnectionField?: 'awsRoleArn' | 'awsCasS3BucketName'
            }
          | undefined
        const hasConnectionTestFailure =
          connectionTestFormikStatus?.arnConnectionStatus === 'failure' ||
          Boolean(connectionTestFormikStatus?.failedConnectionField)
        const requiresTerraformValidation =
          isTerraformStack &&
          formikProps.values.terraformConnectionValidated !== true
        const isSaveDisabled =
          !formikProps.isValid ||
          requiresTerraformValidation ||
          (formikProps.values.isOwnS3Bucket === true &&
            !isTerraformStack &&
            !connectionTested) ||
          hasConnectionTestFailure

        const handleSaveClick = async () => {
          if (!formikProps.dirty) {
            return
          }
          await formikProps.submitForm()
          if (!formikProps.isValid) {
            return
          }
          if (requiresTerraformValidation) {
            return
          }
          if (
            formikProps.values.isOwnS3Bucket === true &&
            !isTerraformStack &&
            !connectionTested
          ) {
            return
          }
          if (hasConnectionTestFailure) {
            return
          }
          await sendSellerNewOnboardingDataFromSettings(formikProps.values)
          formikProps.resetForm({ values: formikProps.values })
        }

        const applyDataFeedsStackFieldReset = async (
          nextMode: 'yes' | 'no'
        ) => {
          await formikProps.setFieldValue('isOwnS3Bucket', nextMode === 'yes')
          await formikProps.setFieldValue('sellerMetadata.awsRoleArn', '')
          await formikProps.setFieldValue(
            'sellerMetadata.awsCasS3BucketName',
            ''
          )
          await formikProps.setFieldValue('sellerMetadata.awsCasSnsTopic', '')
          await formikProps.setFieldValue('sellerMetadata.awsKmsKeyArn', '')
          await formikProps.setFieldValue('terraformConnectionValidated', false)
          if (typeof formikProps.setFieldError === 'function') {
            formikProps.setFieldError('terraformConnectionValidated', undefined)
            formikProps.setFieldError('sellerMetadata.awsRoleArn', undefined)
            formikProps.setFieldError(
              'sellerMetadata.awsCasS3BucketName',
              undefined
            )
            formikProps.setFieldError(
              'sellerMetadata.awsCasSnsTopic',
              undefined
            )
            formikProps.setFieldError('sellerMetadata.awsKmsKeyArn', undefined)
          }
          if (typeof formikProps.setFieldTouched === 'function') {
            formikProps.setFieldTouched(
              'terraformConnectionValidated',
              false,
              false
            )
            formikProps.setFieldTouched(
              'sellerMetadata.awsRoleArn',
              false,
              false
            )
            formikProps.setFieldTouched(
              'sellerMetadata.awsCasS3BucketName',
              false,
              false
            )
            formikProps.setFieldTouched(
              'sellerMetadata.awsCasSnsTopic',
              false,
              false
            )
            formikProps.setFieldTouched(
              'sellerMetadata.awsKmsKeyArn',
              false,
              false
            )
          }
          if (typeof formikProps.setStatus === 'function') {
            formikProps.setStatus({
              arnConnectionStatus: undefined,
              failedConnectionField: undefined,
            })
          }
        }

        const handleStackTypeChange = async (value: StackDeploymentType) => {
          const currentStackType = isTerraformStack
            ? 'terraform'
            : 'cloudformation'
          if (value === currentStackType) {
            return
          }
          await formikProps.setFieldValue('stackType', value)
          await formikProps.setFieldValue('terraformConnectionValidated', false)
          if (typeof formikProps.setFieldError === 'function') {
            formikProps.setFieldError('terraformConnectionValidated', undefined)
          }
          if (typeof formikProps.setFieldTouched === 'function') {
            formikProps.setFieldTouched(
              'terraformConnectionValidated',
              false,
              false
            )
          }
        }

        const handleDataFeedsBucketChoiceChange = async (
          value: 'yes' | 'no'
        ) => {
          const nextMode = value === 'yes' ? 'yes' : 'no'
          const currentMode =
            formikProps.values.isOwnS3Bucket === true ? 'yes' : 'no'
          if (nextMode === currentMode) {
            return
          }
          await applyDataFeedsStackFieldReset(nextMode)
          setConnectionTested(false)
        }

        return (
          <Form className={classes.form}>
            <div className={classes.root}>
              <div className={classes.container}>
                <CloudPartnerDetailsWrapper
                  children={<CloudPartnerDetails />}
                />
                {isMarketplaceCloudSettingsRevampEnabled ? (
                  <FlyOutArnAndDataFeedsWrapper
                    stackType={
                      isTerraformStack ? 'terraform' : 'cloudformation'
                    }
                    onStackTypeChange={handleStackTypeChange}
                    dataFeedsInOwnS3Bucket={
                      formikProps.values.isOwnS3Bucket === true ? 'yes' : 'no'
                    }
                    onDataFeedsInOwnS3BucketChange={
                      handleDataFeedsBucketChoiceChange
                    }
                    disabledDataFeedsInOwnS3BucketChoices={
                      disabledDataFeedsInOwnS3BucketChoices
                    }
                    awsAccountId={formikProps.values.awsAccountId}
                    awsAccountName={formikProps.values.awsAccountName}
                    clientExternalId={
                      formikProps.values.cloudExternalId || accountId
                    }
                    arnChildren={
                      <FlyOutArnDetails
                        key={`${
                          formikProps.values.isOwnS3Bucket === true
                            ? 'yes'
                            : 'no'
                        }-${isTerraformStack ? 'terraform' : 'cloudformation'}`}
                        accountId={accountId}
                        setConnectionTested={setConnectionTested}
                        showConnectionStatus={
                          formikProps.values.isOwnS3Bucket === true
                        }
                      />
                    }
                    dataFeedsChildren={
                      <FlyoutCasProgramSetup
                        key={`${
                          formikProps.values.isOwnS3Bucket === true
                            ? 'yes'
                            : 'no'
                        }-${isTerraformStack ? 'terraform' : 'cloudformation'}`}
                        showCloudFormationDataFeedsSetup={
                          formikProps.values.isOwnS3Bucket === true
                        }
                        enableDataFeedsRevampBehavior={Boolean(
                          isMarketplaceCloudSettingsRevampEnabled
                        )}
                      />
                    }
                  />
                ) : (
                  <>
                    <FlyOutArnDetailsWrapper
                      children={
                        <FlyOutArnDetails
                          accountId={accountId}
                          setConnectionTested={setConnectionTested}
                        />
                      }
                    />
                    <div id="flyoutCasProgramSetupWrapper">
                      <FlyoutCasProgramSetupWrapper
                        children={
                          <FlyoutCasProgramSetup
                            enableDataFeedsRevampBehavior={false}
                          />
                        }
                      />
                    </div>
                  </>
                )}
              </div>
              <FooterV2
                actions={
                  <div className={classes.footerWrapperV2}>
                    <ButtonV2
                      styleType="outlined"
                      data-testid="back-button-aws-cloud-settings"
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </ButtonV2>
                    <ButtonV2
                      data-testid="save-button-cloud-Setting"
                      styleType="primary"
                      onClick={handleSaveClick}
                      loading={loading}
                      disabled={isSaveDisabled}
                    >
                      Save
                    </ButtonV2>
                  </div>
                }
                width={isExternalRoute ? 'calc(100% + 8px)' : undefined}
                customClass={classes.customFooter}
              />
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
